import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useCiAuthStore } from '../auth/ciAuthStore';
import { useCiPoolStore } from '../authorization/ciPoolStore';
import {
  fetchActivityTimeseries,
  fetchDashboardOverview,
  fetchEngagementComparison,
  fetchTopPlayers,
  isAuthorizationError,
} from '../data/ciApi';
import type {
  ActivityDayPoint,
  AuthorizedPool,
  DashboardOverview,
  EngagementComparison,
  TimeWindow,
  TopPlayer,
} from '../data/types';
import { ROSTER_MOVES_TRACKED_SINCE, TIME_WINDOW_DAYS } from '../data/types';
import {
  buildTrendSeries,
  compareMetric,
  completionRate,
  dailyMetricValue,
  engagementScore,
  selectNewJoinersKpi,
  selectWindowedKpi,
  windowDays,
} from '../data/metrics';
import type { TrendMetric } from '../data/metrics';
import {
  PERIOD_PHRASES,
  buildOverviewStory,
  describeTrend,
  formatNumber,
  formatPerDay,
  formatSeasonStart,
  isComparable,
  trendLabel,
} from '../data/overviewStory';
import { KpiCard } from '../components/KpiCard';
import { CiTimeWindowTabs } from '../components/CiTimeWindowTabs';
import { DailyTrendChart } from '../components/charts/DailyTrendChart';
import { RosterMovesNotice } from '../components/RosterMovesNotice';
import { PageViewActivityPanel } from '../components/PageViewActivityPanel';
import { IncompleteReminderPanel } from '../components/IncompleteReminderPanel';

const PERIOD_TITLES: Record<TimeWindow, string> = {
  '7d': '7 derniers jours',
  '30d': '30 derniers jours',
  '90d': '90 derniers jours',
  season: 'Saison complète',
};

const TREND_METRICS: { id: TrendMetric; label: string }[] = [
  { id: 'signings', label: 'Signatures' },
  { id: 'new_joiners', label: 'Nouveaux poolers' },
  { id: 'roster_moves', label: 'Mouvements d’effectif' },
  { id: 'trades', label: 'Échanges' },
];

interface TopPlayers {
  signed: TopPlayer[];
  dropped: TopPlayer[];
  injured: TopPlayer[];
}

const NO_TOP_PLAYERS: TopPlayers = { signed: [], dropped: [], injured: [] };

const generatedAtFormat = new Intl.DateTimeFormat('fr-CA', {
  day: 'numeric',
  month: 'long',
  hour: '2-digit',
  minute: '2-digit',
});

const percentFormat = new Intl.NumberFormat('fr-CA', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Vue d'ensemble : raconte l'activité du pool en quelques phrases, puis
 * donne les chiffres clés, la tendance quotidienne et le détail. Toutes les
 * valeurs proviennent des RPC `ci_*`. Aucune interpolation, aucune projection.
 */
export function OverviewPage({ pools }: { pools: AuthorizedPool[] }) {
  const userId = useCiAuthStore((s) => s.user_id);
  const leagueId = useCiPoolStore((s) => s.selectedLeagueId);
  const [window, setWindow] = useState<TimeWindow>('30d');
  const [trendMetric, setTrendMetric] = useState<TrendMetric>('signings');
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [history, setHistory] = useState<ActivityDayPoint[]>([]);
  const [comparison, setComparison] = useState<EngagementComparison | null>(null);
  const [topPlayers, setTopPlayers] = useState<TopPlayers>(NO_TOP_PLAYERS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reminderOpen, setReminderOpen] = useState(false);

  useEffect(() => {
    setReminderOpen(false);
  }, [leagueId]);

  useEffect(() => {
    let cancelled = false;
    if (!userId || !leagueId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    (async () => {
      const days = TIME_WINDOW_DAYS[window];
      const [ov, ts, comp, signed, dropped, injured] = await Promise.all([
        fetchDashboardOverview(userId, leagueId),
        fetchActivityTimeseries(userId, leagueId, Math.min(windowDays(window) + 7, 400)),
        window === 'season'
          ? Promise.resolve({ data: null, error: null })
          : fetchEngagementComparison(userId, leagueId, windowDays(window)),
        fetchTopPlayers(userId, leagueId, 'signed', days, 3),
        fetchTopPlayers(userId, leagueId, 'dropped', days, 1),
        fetchTopPlayers(userId, leagueId, 'injured', days, 1),
      ]);
      if (cancelled) return;
      const blocking = ov.error ?? ts.error ?? comp.error;
      if (blocking) {
        setError(
          isAuthorizationError(blocking)
            ? 'Accès refusé pour ce pool.'
            : 'Erreur lors du chargement de la vue d’ensemble.'
        );
        setLoading(false);
        return;
      }
      setOverview(ov.data);
      setHistory(ts.data ?? []);
      setComparison(comp.data);
      // Les joueurs du moment sont un complément : une erreur les masque sans bloquer la page.
      setTopPlayers({
        signed: signed.data ?? [],
        dropped: dropped.data ?? [],
        injured: injured.data ?? [],
      });
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, leagueId, window]);

  if (!leagueId) {
    return <div className="ci-empty">Sélectionnez un pool pour afficher les indicateurs.</div>;
  }
  if (error) return <div className="ci-error">{error}</div>;
  if (!overview) {
    return <div className="ci-empty">{loading ? 'Chargement…' : 'Aucune donnée pour ce pool.'}</div>;
  }

  const days = windowDays(window);
  // La série ne remonte pas avant le début de saison : fenêtre réelle en jours.
  const activeDays = history.slice(-days).length || days;
  const period = PERIOD_PHRASES[window];
  const previous = isComparable(comparison) ? comparison : null;
  const seasonStart = overview.season_start ? formatSeasonStart(overview.season_start) : null;
  const total = overview.total_poolers ?? 0;
  const story = buildOverviewStory({
    window,
    overview,
    comparison,
    history,
    topSigned: topPlayers.signed[0] ?? null,
  });

  const signings = comparison?.signings_current ?? selectWindowedKpi(overview, 'signings', window);
  const joiners = comparison?.new_poolers_current ?? selectNewJoinersKpi(overview, window);
  const rate = completionRate(overview);
  const trades = selectWindowedKpi(overview, 'trades', window);
  const drops = selectWindowedKpi(overview, 'drops', window);
  const placed = selectWindowedKpi(overview, 'ltir_placed', window);
  const reactivated = selectWindowedKpi(overview, 'ltir_reactivated', window);

  // Métriques du graphique : on masque celles qui n'ont aucune donnée dans l'historique reçu.
  const availableMetrics = TREND_METRICS.filter(
    (m) => m.id === 'signings' || history.some((point) => dailyMetricValue(point, m.id) > 0)
  );
  const activeMetric = availableMetrics.find((m) => m.id === trendMetric) ?? availableMetrics[0];
  const trendSeries = buildTrendSeries(history, activeMetric.id, days);

  return (
    <div className={loading ? 'ci-overview is-refreshing' : 'ci-overview'} aria-busy={loading}>
      <div className="ci-page-head">
        <div>
          <h1 className="ci-page-head__title">Vue d’ensemble</h1>
          <p className="ci-page-head__meta">
            Période : {PERIOD_TITLES[window].toLowerCase()} · Données au{' '}
            {generatedAtFormat.format(new Date(overview.generated_at))}
          </p>
          {seasonStart && (
            <p className="ci-page-head__meta">
              Saison commencée le {seasonStart} : l’activité antérieure (phase de création) n’est pas comptée.
            </p>
          )}
        </div>
        <CiTimeWindowTabs value={window} onChange={setWindow} />
      </div>

      <RosterMovesNotice />

      <div className="ci-story-grid">
        <section className="ci-section ci-story" aria-labelledby="ci-story-title">
          <h2 id="ci-story-title" className="ci-story__eyebrow">En bref</h2>
          <p className="ci-story__headline">{story.headline}</p>
          {story.points.length > 0 && (
            <ul className="ci-story__points">
              {story.points.map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          )}
        </section>
        {story.actions.length > 0 && (
          <section className="ci-section ci-actions" aria-labelledby="ci-actions-title">
            <h2 id="ci-actions-title" className="ci-story__eyebrow">Pistes d’action</h2>
            <ol className="ci-actions__list">
              {story.actions.map((action) => (
                <li key={action.id}>
                  {action.text}
                  {action.id === 'incomplete-teams' && !reminderOpen && (
                    <>
                      {' '}
                      <button
                        type="button"
                        className="ci-btn ci-btn--sm ci-actions__button"
                        onClick={() => setReminderOpen(true)}
                      >
                        Préparer le courriel
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ol>
            <Link to="/ci-dashboard/opportunities" className="ci-actions__link">
              Voir les opportunités de contenu
            </Link>
          </section>
        )}
      </div>

      {reminderOpen && (
        <IncompleteReminderPanel
          leagueId={leagueId}
          leagueName={pools.find((pool) => pool.league_id === leagueId)?.league_name ?? ''}
          onClose={() => setReminderOpen(false)}
        />
      )}

      <div className="ci-kpis ci-kpis--overview">
        {comparison ? (
          <KpiCard
            label="Poolers actifs"
            value={`${engagementScore(comparison.poolers_signing_current, total)} %`}
            sub={`${formatNumber(comparison.poolers_signing_current)} sur ${formatNumber(total)} ont signé au moins un joueur`}
            delta={
              previous
                ? trendLabel(
                    describeTrend(previous.poolers_signing_current, previous.poolers_signing_previous),
                    window
                  )
                : null
            }
          />
        ) : (
          <KpiCard label="Poolers inscrits" value={formatNumber(total)} />
        )}
        <KpiCard
          label="Signatures d’agents libres"
          value={formatNumber(signings)}
          sub={window === 'season' ? undefined : `Environ ${formatPerDay(signings, activeDays)} par jour`}
          delta={previous ? trendLabel(describeTrend(signings, previous.signings_previous), window) : null}
        />
        {window !== 'season' && (
          <KpiCard
            label="Nouveaux poolers"
            value={formatNumber(joiners)}
            delta={previous ? trendLabel(describeTrend(joiners, previous.new_poolers_previous), window) : null}
          />
        )}
        <KpiCard
          label="Équipes complètes"
          value={`${rate} %`}
          meter={rate}
          sub={`${formatNumber(overview.completed_builds ?? 0)} complètes · ${formatNumber(overview.incomplete_builds ?? 0)} à compléter`}
        />
        {(overview.trades_all_time ?? 0) > 0 && (
          <KpiCard label="Échanges conclus" value={formatNumber(trades)} />
        )}
      </div>

      {userId && leagueId && (
        <PageViewActivityPanel
          userId={userId}
          leagueId={leagueId}
          days={TIME_WINDOW_DAYS[window]}
        />
      )}

      <section className="ci-section">
        <div className="ci-section__header">
          <div>
            <h2 className="ci-section__title">Activité jour par jour</h2>
            <p className="ci-section__meta ci-section__meta--tight">
              Chaque barre représente une journée ; la ligne lisse les variations sur 7 jours.
              {activeMetric.id === 'roster_moves' && (
                <strong> Libérations et LTIR comptées seulement à partir du {ROSTER_MOVES_TRACKED_SINCE}.</strong>
              )}
            </p>
          </div>
          {availableMetrics.length > 1 && (
            <div className="ci-tabs" role="tablist" aria-label="Métrique du graphique">
              {availableMetrics.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  role="tab"
                  aria-selected={activeMetric.id === m.id}
                  className={`ci-tabs__btn${activeMetric.id === m.id ? ' is-active' : ''}`}
                  onClick={() => setTrendMetric(m.id)}
                >
                  {m.label}
                </button>
              ))}
            </div>
          )}
        </div>
        {trendSeries.length === 0 ? (
          <div className="ci-empty">Aucune activité sur cette période.</div>
        ) : (
          <DailyTrendChart data={trendSeries} label={activeMetric.label} />
        )}
      </section>

      <div className="ci-grid-2 ci-grid-2--top">
        <section className="ci-section">
          <h2 className="ci-section__title">Mouvements d’effectif</h2>
          <p className="ci-section__meta">
            <strong>Comptés seulement à partir du {ROSTER_MOVES_TRACKED_SINCE} : aucun historique avant cette date.</strong>
          </p>
          <dl className="ci-figures">
            <div>
              <dt>Libérations</dt>
              <dd>{formatNumber(drops)}</dd>
            </div>
            <div>
              <dt>Mises en LTIR</dt>
              <dd>{formatNumber(placed)}</dd>
            </div>
            <div>
              <dt>Réactivations LTIR</dt>
              <dd>{formatNumber(reactivated)}</dd>
            </div>
          </dl>
          <Link to="/ci-dashboard/activity" className="ci-section__link">
            Voir chaque mouvement dans Activité
          </Link>
        </section>

        <section className="ci-section">
          <h2 className="ci-section__title">Joueurs du moment</h2>
          <p className="ci-section__meta">
            {window === 'season' ? 'Depuis le début de la saison' : `Sur les ${PERIOD_TITLES[window]}`}
          </p>
          {topPlayers.signed.length === 0 &&
          topPlayers.dropped.length === 0 &&
          topPlayers.injured.length === 0 ? (
            <div className="ci-empty">Aucun mouvement de joueur sur cette période.</div>
          ) : (
            <dl className="ci-player-highlights">
              {topPlayers.signed.length > 0 && (
                <div>
                  <dt>Les plus signés</dt>
                  <dd>
                    <ol>
                      {topPlayers.signed.map((p) => (
                        <li key={p.player_id}>
                          <span>{p.player_name ?? `Joueur #${p.player_id}`}</span>
                          <span className="ci-player-highlights__count">{formatNumber(p.event_count)}</span>
                        </li>
                      ))}
                    </ol>
                  </dd>
                </div>
              )}
              {topPlayers.dropped[0] && (
                <div>
                  <dt>Le plus libéré (depuis le {ROSTER_MOVES_TRACKED_SINCE})</dt>
                  <dd>
                    {topPlayers.dropped[0].player_name ?? `Joueur #${topPlayers.dropped[0].player_id}`}{' '}
                    <span className="ci-player-highlights__count">
                      {formatNumber(topPlayers.dropped[0].event_count)}
                    </span>
                  </dd>
                </div>
              )}
              {topPlayers.injured[0] && (
                <div>
                  <dt>Le plus placé en LTIR (depuis le {ROSTER_MOVES_TRACKED_SINCE})</dt>
                  <dd>
                    {topPlayers.injured[0].player_name ?? `Joueur #${topPlayers.injured[0].player_id}`}{' '}
                    <span className="ci-player-highlights__count">
                      {formatNumber(topPlayers.injured[0].event_count)}
                    </span>
                  </dd>
                </div>
              )}
            </dl>
          )}
          <Link to="/ci-dashboard/players" className="ci-section__link">
            Voir tous les classements de joueurs
          </Link>
        </section>
      </div>

      <details className="ci-section ci-details">
        <summary>Tableau détaillé et méthode de calcul</summary>
        {previous ? (
          <div className="ci-table__wrap">
            <table className="ci-table">
              <thead>
                <tr>
                  <th>Métrique</th>
                  <th className="num">{PERIOD_TITLES[window]}</th>
                  <th className="num">{period.previous.replace(/^./, (c) => c.toUpperCase())}</th>
                  <th className="num">Variation</th>
                </tr>
              </thead>
              <tbody>
                {(
                  [
                    ['Signatures d’agents libres', previous.signings_current, previous.signings_previous],
                    ['Poolers ayant signé', previous.poolers_signing_current, previous.poolers_signing_previous],
                    ['Nouveaux poolers', previous.new_poolers_current, previous.new_poolers_previous],
                    ...(previous.releases_current !== undefined
                      ? [
                          ['Libérations', previous.releases_current ?? 0, previous.releases_previous ?? 0],
                          ['Mises en LTIR', previous.ltir_placed_current ?? 0, previous.ltir_placed_previous ?? 0],
                          ['Réactivations LTIR', previous.ltir_reactivated_current ?? 0, previous.ltir_reactivated_previous ?? 0],
                        ]
                      : []),
                  ] as [string, number, number][]
                ).map(([label, current, previous]) => {
                  const row = compareMetric(current, previous);
                  return (
                    <tr key={label}>
                      <td>{label}</td>
                      <td className="num">{formatNumber(row.current)}</td>
                      <td className="num">{formatNumber(row.previous)}</td>
                      <td className="num">
                        {row.percentChange === null
                          ? row.current === 0
                            ? '—'
                            : 'Nouveau'
                          : `${row.percentChange > 0 ? '+' : ''}${percentFormat.format(row.percentChange)} %`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="ci-section__meta">
            {comparison
              ? `Pas de comparaison : les ${period.previous} commencent avant le début de la saison${seasonStart ? ` (${seasonStart})` : ''}.`
              : 'La comparaison entre périodes est disponible pour 7, 30 ou 90 jours.'}
          </p>
        )}
        <ul className="ci-data-limits">
          {seasonStart && (
            <li>
              <strong>Début de saison</strong> : les statistiques comptent à partir du{' '}
              {seasonStart} (heure de Montréal). Les signatures faites pendant la création des
              équipes et avant cette date sont exclues ; le fil Activité garde l’historique complet.
            </li>
          )}
          <li>
            <strong>Poolers actifs</strong> : part des poolers inscrits qui ont signé au moins un
            agent libre pendant la période. Les pages consultées sont présentées séparément et ne
            sont pas comptées dans cette mesure.
          </li>
          <li>
            <strong>Libérations et blessures (LTIR)</strong> : toutes les statistiques de
            libérations, mises en LTIR et réactivations sont comptées à partir du{' '}
            {ROSTER_MOVES_TRACKED_SINCE}, date de début de leur suivi ; aucune donnée antérieure
            n’existe. Une comparaison avec une période précédente plus ancienne affiche donc
            « Nouveau ». Elles sont informatives et n’entrent pas dans la part de poolers actifs.
          </li>
          <li>Postes remplis dans les alignements : {formatNumber(overview.roster_slots_filled ?? 0)}.</li>
        </ul>
      </details>
    </div>
  );
}
